import React from 'react';
import { useTranslation } from 'react-i18next';
import ReactPlaceholder from 'react-placeholder';
import { mapDeviceType } from 'commons/src/commonFunctions';
import DeviceStatusPill from 'commons/src/components/pills/DeviceStatusPill';
import { DeviceTypeNames } from 'commons/src/models/commonEnums';
import { AnyDeviceType } from 'commons/src/models/commonTypeScript';
import DeviceHealthStatus from 'commons/src/models/deviceHealthStatus';
import { BUILDING_HEALTH_TABLE_LENGTH } from '../../constants';
import { BuildingHealthRowData } from '../../models/buildingHealthModels';
import MaterialTable from '../materialTable/MaterialTable';
import MaterialTablePlaceholder from '../materialTable/MaterialTablePlaceholder';
import { MaterialTableColumn } from '../materialTable/models';
import styles from './BuildingDevicesTable.module.scss';

export type BuildingDevicesTableProps = {
    locationId: string;
    data: BuildingHealthRowData[];
    totalData: number;
    loading?: boolean;
    streaming?: boolean;
    error?: unknown;
    tableIdPrefix: string;
};

const BuildingDevicesTable = ({
    locationId,
    data,
    totalData,
    loading,
    streaming,
    error,
    tableIdPrefix,
}: BuildingDevicesTableProps): React.ReactElement => {
    const { t: txt } = useTranslation();

    const columns: MaterialTableColumn[] = [
        {
            accessor: 'segmentName',
            label: txt('HealthStatus.Name'),
            sortable: true,
            width: '150px',
            locked: true,
            renderer: ({ rowData }): React.ReactNode => {
                const name = rowData.segmentName as string;
                return (
                    <span title={name} className={styles.deviceName}>
                        {name}
                    </span>
                );
            },
        },
        {
            accessor: 'type',
            label: txt('HealthStatus.Type'),
            sortable: true,
            width: '130px',
            renderer: ({ rowData }): React.ReactNode => {
                const type = rowData?.type as AnyDeviceType;
                if (!type) return null;
                return <span>{txt(`${mapDeviceType(type)}FullName`)}</span>;
            },
        },
        {
            accessor: 'serialNumber',
            label: txt('BuildingHealth.SerialNumber'),
            sortable: true,
            width: '100px',
        },
        {
            accessor: 'floor',
            label: txt('HealthStatus.Floor'),
            sortable: true,
            width: '70px',
            renderer: ({ rowData }): React.ReactNode => {
                const type = rowData?.type;
                if (type === DeviceTypeNames.hub) return 'n/a';

                const floor = rowData?.floor as string;
                return floor ?? '-';
            },
        },
        {
            accessor: 'healthStatus',
            label: txt('HealthStatus.Status'),
            sortable: true,
            renderer: ({ rowData }): React.ReactNode => {
                const type = rowData?.type as AnyDeviceType;
                const deviceType = type === DeviceTypeNames.hub ? 'hub' : 'device';
                return <DeviceStatusPill status={rowData.healthStatus as DeviceHealthStatus} deviceType={deviceType} />;
            },
            width: '280px',
        },
    ];

    return (
        <main className={styles.bodyWrapper} data-testid={`collapse_content_${locationId}`}>
            <ReactPlaceholder ready={!loading} showLoadingAnimation customPlaceholder={<MaterialTablePlaceholder />}>
                {error ? <div className={styles.errorMessage}>Error loading building data</div> : null}
                <MaterialTable
                    tableId={`${tableIdPrefix}_table_building_${locationId}`}
                    columns={columns}
                    data={data}
                    isReady={!streaming}
                    idKey="serialNumber"
                    pagination={BUILDING_HEALTH_TABLE_LENGTH}
                    totalDataLength={totalData}
                />
            </ReactPlaceholder>
        </main>
    );
};

export default BuildingDevicesTable;
